import type { WorkspaceKey } from '@festina-life/api'
import { IconButton } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { ConfirmationBox } from '@festina-life/ui/structure'
import { useState } from 'react'

import { isEqualWorkspace } from '../../workspace/helpers'
import { useOperationsContext } from '../../workspace/operationsContext'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { OperationsLoadedButton } from '../operationsButton/OperationsLoadedButton'

type Props = {
  variant: 'icon' | 'manage'
  workspaceKey: WorkspaceKey
}

export const DeleteWorkspaceButton = ({ variant, workspaceKey }: Props) => {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)
  const { currentWorkspaceKey, onDelete } = useWorkspaceManagerContext()
  const { isLoading } = useOperationsContext()
  const translate = useTranslate()

  const handleRemoveWorkspace = async () => {
    await onDelete(workspaceKey)
    setIsDeleteConfirmationOpen(false)
  }

  const handleDelete = () => {
    setIsDeleteConfirmationOpen(true)
  }
  const isButtonDisabled =
    isEqualWorkspace(workspaceKey, currentWorkspaceKey) || isLoading

  return (
    <>
      <ConfirmationBox
        isOpen={isDeleteConfirmationOpen}
        onCancel={() => setIsDeleteConfirmationOpen(false)}
        title={translate('workspace.deleteConfirmation.title', {
          name: workspaceKey.name,
        })}
        onAccept={handleRemoveWorkspace}
        confirmLabel={translate('delete')}
        isDanger
        panelClassName="max-w-lg"
      >
        {translate('workspace.deleteConfirmation.description')}
      </ConfirmationBox>
      {variant === 'icon' ? (
        <IconButton
          tip={translate('delete')}
          iconName="Trash"
          isDanger
          className="invisible group-hover:visible p-0!"
          onClick={handleDelete}
          disabled={isButtonDisabled}
        />
      ) : (
        <OperationsLoadedButton
          startIcon="Trash"
          isDanger
          onClick={handleDelete}
          disabled={isButtonDisabled}
        >
          {translate('workspaceDetailsPanel.button.deleteWorkspace')}
        </OperationsLoadedButton>
      )}
    </>
  )
}
